import React, { useState, useEffect } from 'react';

const TreasureHuntMath = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 0 });
  const [treasures, setTreasures] = useState([]);
  const [currentClue, setCurrentClue] = useState(null);
  const [foundTreasures, setFoundTreasures] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [map, setMap] = useState([]);
  const [showClue, setShowClue] = useState(false);

  const generateMathClue = () => {
    const clueTypes = [
      {
        type: 'coordinate',
        generate: () => {
          const x = Math.floor(Math.random() * 8);
          const y = Math.floor(Math.random() * 8);
          const num1 = Math.floor(Math.random() * 10) + 1;
          const num2 = Math.floor(Math.random() * 10) + 1;
          return {
            question: `Find treasure at (${num1} + ${x - num1}, ${num2} × ${Math.floor(y / num2)})`,
            answer: { x, y },
            hint: 'Solve the coordinates!'
          };
        }
      },
      {
        type: 'steps',
        generate: () => {
          const x = Math.floor(Math.random() * 8);
          const y = Math.floor(Math.random() * 8);
          const stepsX = Math.abs(x - playerPos.x);
          const stepsY = Math.abs(y - playerPos.y);
          const totalSteps = stepsX + stepsY;
          const num1 = Math.floor(Math.random() * 10) + totalSteps;
          const num2 = num1 - totalSteps;
          return {
            question: `Treasure is ${num1} - ${num2} = ? steps away (total)`,
            answer: { x, y },
            hint: `Go ${x > playerPos.x ? 'right' : 'left'} and ${y > playerPos.y ? 'down' : 'up'}!`
          };
        }
      },
      {
        type: 'area',
        generate: () => {
          const x = Math.floor(Math.random() * 8);
          const y = Math.floor(Math.random() * 8);
          const area = (x + 1) * (y + 1);
          return {
            question: `Treasure in rectangle with area ${area}. Width is ${x + 1}, find it!`,
            answer: { x, y },
            hint: 'Area = length × width'
          };
        }
      }
    ];

    const clueType = clueTypes[Math.floor(Math.random() * clueTypes.length)];
    return clueType.generate();
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setFoundTreasures(0);
    setTimeLeft(60);
    setPlayerPos({ x: 0, y: 0 });
    
    const newMap = Array(8).fill(null).map(() => Array(8).fill('empty'));
    const newTreasures = [];
    
    for(let i = 0; i < 5; i++) {
      let x, y;
      do {
        x = Math.floor(Math.random() * 8);
        y = Math.floor(Math.random() * 8);
      } while(newMap[y][x] !== 'empty' || (x === 0 && y === 0));
      
      newMap[y][x] = 'treasure';
      newTreasures.push({ x, y, found: false });
    }
    
    setMap(newMap);
    setTreasures(newTreasures);
    setCurrentClue(generateMathClue());
  };

  useEffect(() => {
    if(gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if(t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  const movePlayer = (dx, dy) => {
    const newX = Math.max(0, Math.min(7, playerPos.x + dx));
    const newY = Math.max(0, Math.min(7, playerPos.y + dy));
    setPlayerPos({ x: newX, y: newY });

    if(map[newY][newX] === 'treasure') {
      const treasure = treasures.find(t => t.x === newX && t.y === newY && !t.found);
      if(treasure) {
        treasure.found = true;
        setFoundTreasures(f => f + 1);
        setScore(s => s + 200);
        setTimeLeft(t => t + 10);
        
        const newMap = [...map];
        newMap[newY][newX] = 'found';
        setMap(newMap);
        
        if(foundTreasures + 1 >= 5) {
          setLevel(l => l + 1);
          setTimeout(() => startGame(), 1000);
        } else {
          setCurrentClue(generateMathClue());
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if(gameState !== 'playing') return;
    
    switch(e.key) {
      case 'ArrowUp':
        movePlayer(0, -1);
        break;
      case 'ArrowDown':
        movePlayer(0, 1);
        break;
      case 'ArrowLeft':
        movePlayer(-1, 0);
        break;
      case 'ArrowRight':
        movePlayer(1, 0);
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, playerPos, map, treasures]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-700 to-yellow-900 p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-200">
          🗺️ Treasure Hunt Math 💎
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏴‍☠️</div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Ahoy, Treasure Hunter!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Solve math clues to find hidden treasures!<br/>
              Use arrow keys to explore the island
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START HUNT
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Treasures: {foundTreasures}/5</div>
              <div className={`text-xl font-bold ${timeLeft < 20 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Time: {timeLeft}s
              </div>
            </div>

            {currentClue && (
              <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-lg p-6 mb-4">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="text-lg font-bold mb-2">🗺️ Math Clue:</div>
                    <div className="text-2xl font-bold">{currentClue.question}</div>
                  </div>
                  <button
                    onClick={() => setShowClue(!showClue)}
                    className="bg-white text-yellow-700 px-4 py-2 rounded-lg font-bold hover:bg-yellow-100"
                  >
                    {showClue ? 'Hide' : 'Show'} Hint
                  </button>
                </div>
                {showClue && (
                  <div className="mt-4 bg-yellow-700 p-3 rounded-lg">
                    💡 {currentClue.hint}
                  </div>
                )}
              </div>
            )}

            <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-6">
              <div className="grid grid-cols-8 gap-2">
                {map.map((row, y) => (
                  row.map((cell, x) => (
                    <div
                      key={`${x}-${y}`}
                      className={`aspect-square rounded-lg flex items-center justify-center text-3xl font-bold transition-all ${
                        playerPos.x === x && playerPos.y === y
                          ? 'bg-blue-500 shadow-lg scale-110'
                          : cell === 'found'
                          ? 'bg-yellow-300'
                          : cell === 'treasure'
                          ? 'bg-green-700'
                          : 'bg-green-600 hover:bg-green-500'
                      }`}
                    >
                      {playerPos.x === x && playerPos.y === y ? '🧭' : cell === 'found' ? '✓' : ''}
                    </div>
                  ))
                ))}
              </div>
            </div>

            <div className="text-center mt-4 text-white text-lg">
              Use Arrow Keys to Move • Find all 5 treasures!
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">
              {foundTreasures >= 5 ? '🏆' : '⏰'}
            </div>
            <h2 className="text-4xl font-bold mb-4 text-gray-800">
              {foundTreasures >= 5 ? 'All Treasures Found!' : 'Time\'s Up!'}
            </h2>
            <div className="text-3xl mb-4 text-yellow-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">
              Treasures Found: {foundTreasures}/5 • Level: {level}
            </div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              HUNT AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreasureHuntMath;
